import { sandhillsRequest, insertURLParameter } from '@Components/Utilities/SandhillsUtility';
import { cookieNames } from '@Components/Utilities/SandhillsConstants';
import { getCookie } from '@Components/Utilities/CookieHelper';

// returns a promise
// use this when we must block execution of the resolve callback function until the user grants permission to access location
// good use for this: distance sort if user wants to use current location we can't run the sort until the user grants permission
const getUserPosition = (useCookiesFirst, reverseGeocodingUrl, setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl) => {
    return getCurrentUserPosition(useCookiesFirst, false, reverseGeocodingUrl, setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl);
};

// this function will return a promise, the difference between this and the normal getUserPosition is:
// this function will check first if the location cookies are available, if so use them, otherwise check if permission is granted to access location
// if neither condition is true this function returns a null locaiton, asynchronously asks for location in the background and executes the resolve callback on the null
// good use for this: banner ads we would like to set location before showing ads but CANNOT wait until the user grants persmission for location access
// so instead try to get location if we can without waiting for permission, if we can't then generate the ads and ask for permission,
// ideally user grants so on next page load we have location ahead of time
const getUserPositionNonBlocking = (useCookiesFirst, setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl) => {
    return getCurrentUserPosition(useCookiesFirst, true, null, setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl);
};

const getFormattedLocationFromCurrentCoordinates = (reverseGeocodingUrl) => {
    return sandhillsRequest({
        url: reverseGeocodingUrl,
        errorHandling: {
            useConsole: false,
            useAlert: false
        }
    }).then(formattedAddress => {
        return formattedAddress;
    });
};

const isPositionValid = (currentPosition) => {
    return currentPosition && !(isNaN(currentPosition.longitude) || currentPosition.longitude === 0.0 || isNaN(currentPosition.latitude) || currentPosition.latitude === 0.0
        || currentPosition.longitude === '' || currentPosition.latitude === '');
};

const getCoordinatesFromAddress = (address, ajaxGetCoordinatesURL) => {
    return new Promise((resolve, reject) => {
        if (address && ajaxGetCoordinatesURL) {
            let updatedAjaxGetCoordinatesURL = insertURLParameter(ajaxGetCoordinatesURL, 'locationString', address);
            sandhillsRequest({
                url: updatedAjaxGetCoordinatesURL,
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                if (!data.Coordinates || !data.Coordinates.Latitude || !data.Coordinates.Longitude) {
                    resolve(getEmptyLocation());
                } else {
                    let newPos = { latitude: data.Coordinates.Latitude, longitude: data.Coordinates.Longitude, formattedAddress: data.FormattedAddress };
                    resolve(newPos);
                }
            }).catch((error) => {
                reject(error);
            });
        } else {
            reject("Address or AjaxGetCoordinatesURL is not defined");
        }
    });
};

///////////////////////////////////
// The below functions are helpers not meant to be used outside this file, don't export these
///////////////////////////////////

const getCurrentUserPosition = (useCookiesFirst, shouldNotBlockResolve, reverseGeocodingUrl, setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl) => {
    return new Promise((resolve, reject) => {
        if (shouldNotBlockResolve) {
            isCurrentLocationBeingUsed(setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl).then((location) => {
                if (!location) {
                    getUserPosition(false, null, setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl);
                    resolve(null);
                }
                resolve(location);
            });
        } else {
            Promise.resolve(useCookiesFirst ? getCurrentUserLocationFromCookie(getDecryptedCoordinatesFromUserLocationCookieUrl) : null).then(curLocation => {
                if (!curLocation || !isPositionValid(curLocation)) {
                    if (!getCookie(cookieNames.DisableGeolocationCookie) && navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(async (position) => {
                            await setCurrentUserLocationCookie(position.coords.latitude, position.coords.longitude, setUserLocationCookieUrl);
                            let currentLocationFromCookies = await getCurrentUserLocationFromCookie(getDecryptedCoordinatesFromUserLocationCookieUrl);
                            if (reverseGeocodingUrl) {
                                getFormattedLocationFromCurrentCoordinates(reverseGeocodingUrl)
                                    .then(formattedAddress => {
                                        currentLocationFromCookies.formattedAddress = formattedAddress;
                                        resolve(currentLocationFromCookies);
                                    })
                                    .catch(error => {
                                        reject(error);
                                    });
                            } else {
                                resolve(currentLocationFromCookies);
                            }
                        }, () => {
                            resolve(getEmptyLocation());
                        });
                    } else {
                        resolve(getEmptyLocation());
                    }
                } else {
                    if (reverseGeocodingUrl) {
                        getFormattedLocationFromCurrentCoordinates(reverseGeocodingUrl)
                            .then(formattedAddress => {
                                curLocation.formattedAddress = formattedAddress;
                                resolve(curLocation);
                            })
                            .catch(error => {
                                reject(error);
                            });
                    } else {
                        resolve(curLocation);
                    }
                }
            });
        }
    });
};

const isCurrentLocationBeingUsed = (setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl) => {
    return new Promise((resolve, reject) => {
        Promise.resolve(getCurrentUserLocationFromCookie(getDecryptedCoordinatesFromUserLocationCookieUrl)).then(curLocation => {
            if (!curLocation || !isPositionValid(curLocation)) {
                if (!getCookie(cookieNames.DisableGeolocationCookie) && navigator.permissions && navigator.permissions.query) {
                    navigator.permissions.query({ name: 'geolocation' }).then((permissions) => {
                        if (permissions.state && permissions.state == 'granted') {
                            getUserPosition(false, null, setUserLocationCookieUrl, getDecryptedCoordinatesFromUserLocationCookieUrl).then((pos) => { resolve(pos); });
                        } else {
                            resolve(null);
                        }

                    });
                } else {
                    resolve(null);
                }
            } else {
                resolve(curLocation);
            }
        });
    });
};

const getEmptyLocation = () => {
    return { latitude: NaN, longitude: NaN };
};

const getCurrentUserLocationFromCookie = (getDecryptedCoordinatesFromUserLocationCookieUrl) => {
    return sandhillsRequest({
        url: getDecryptedCoordinatesFromUserLocationCookieUrl,
        errorHandling: {
            useConsole: false,
            useAlert: false
        }
    }).then(coordinates => {
        if (coordinates) {
            return { latitude: parseFloat(coordinates.Latitude), longitude: parseFloat(coordinates.Longitude) };
        }
        else {
            return getEmptyLocation();
        }
    });
};

const setCurrentUserLocationCookie = (latitude, longitude, setUserLocationCookieUrl) => {
    let updatedAjaxUserLocationUrl = insertURLParameter(setUserLocationCookieUrl, 'userLatitude', latitude);
    updatedAjaxUserLocationUrl = insertURLParameter(updatedAjaxUserLocationUrl, 'userLongitude', longitude);

    return sandhillsRequest({
        url: updatedAjaxUserLocationUrl,
        errorHandling: {
            useConsole: false,
            useAlert: false
        }
    });
};

export {
    getUserPosition,
    isPositionValid,
    getUserPositionNonBlocking,
    getCoordinatesFromAddress
};